import { useState, useCallback, useRef } from 'react'
import type { DuplicateRoleGroup, SimilarRolePair } from '@shared/roster-validation'
import { useApi } from '../../contexts/ApiContext'
import RosterSelectionModal from '../../components/RosterSelectionModal'
import RosterQualityModal from '../../components/RosterQualityModal'
import { RosterErrorList } from '../../components/useRosterValidation'
import type { UploadZone } from '../../utils/fileZones'
import { ZONE_EXTS, ZONE_REJECT_MESSAGE, splitByZone } from '../../utils/fileZones'
import { validateRosterFile } from '../../utils/validateRoster'
import type { RosterIssue } from '../../utils/validateRoster'

interface Props {
  onAnalyzeDocuments?: (files: Array<{ base64: string; name: string }>) => Promise<void>
  clientName: string
  /**
   * Whether an AI provider API key is configured. Defaults to true so existing
   * callers/tests that don't care about this precondition are unaffected.
   * Every roster/document upload requires the AI pipeline (WI 2211, PBI 2180) —
   * without this, missing the key wasn't discovered until the generation job
   * failed, with only the page-level banner as warning (code review finding).
   */
  hasApiKey?: boolean
}

function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let binary = ''
  for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i])
  return btoa(binary)
}

export default function FileUpload({ onAnalyzeDocuments, clientName, hasApiKey = true }: Props) {
  const api = useApi()
  const [dragging, setDragging] = useState<UploadZone | null>(null)
  // Focus is tracked in state rather than left to :focus-visible so the keyboard
  // indicator is explicit and does not depend on Tailwind's ring utilities,
  // which resolve to box-shadow: none in this build.
  const [focusedZone, setFocusedZone] = useState<UploadZone | null>(null)
  const [rosterFiles, setRosterFiles] = useState<File[]>([])
  const [contextFiles, setContextFiles] = useState<File[]>([])
  // Wrong-type drops are reported under the zone that rejected them.
  const [zoneError, setZoneError] = useState<{ zone: UploadZone; message: string } | null>(null)
  const [parseError, setParseError] = useState<string | null>(null)
  const [status, setStatus] = useState<'idle' | 'extracting' | 'analyzing'>('idle')
  // Blocking roster problems, by sheet/row/column (WI 2269).
  const [rosterErrors, setRosterErrors] = useState<RosterIssue[]>([])
  // Set when more than one roster file is queued.
  const [rosterSelection, setRosterSelection] = useState<{ rosters: File[]; docs: File[] } | null>(null)
  // Duplicate / similar-role report awaiting acknowledgement.
  const [rosterReport, setRosterReport] = useState<{
    fileName: string
    duplicateRoles: DuplicateRoleGroup[]
    similarRoles: SimilarRolePair[]
  } | null>(null)
  const pendingAfterRosterReportRef = useRef<(() => void) | null>(null)

  const addFiles = (incoming: FileList | File[], zone: UploadZone) => {
    const { accepted, rejected } = splitByZone(Array.from(incoming), zone)

    // A file of the wrong family is reported against this zone rather than
    // dropped in silence, which is how the single-zone version behaved.
    setZoneError(rejected.length === 0 ? null : { zone, message: ZONE_REJECT_MESSAGE[zone] })
    if (accepted.length === 0) return

    const setFiles = zone === 'roster' ? setRosterFiles : setContextFiles
    setFiles((prev) => {
      const names = new Set(prev.map((f) => f.name))
      return [...prev, ...accepted.filter((f) => !names.has(f.name))]
    })
    setParseError(null)
  }

  const removeFile = (name: string, zone: UploadZone) => {
    const setFiles = zone === 'roster' ? setRosterFiles : setContextFiles
    setFiles((prev) => prev.filter((f) => f.name !== name))
    setRosterErrors([])
  }

  /**
   * AI-powered extraction: every queued file — documents and roster files alike
   * (XLSX, CSV, or a Talent Navigator JSON export, PBI 2180) — is sent to the
   * backend pipeline.
   */
  const runDocumentAnalysis = async (docFiles: File[], rosters: File[]) => {
    if (!clientName.trim()) { setParseError('Please enter a client name before uploading documents.'); return }
    if (!onAnalyzeDocuments) { setParseError('Document analysis is not available.'); return }

    try {
      setStatus('extracting')
      const filePayloads: Array<{ base64: string; name: string }> = []
      for (const file of [...docFiles, ...rosters]) {
        const base64 = arrayBufferToBase64(await file.arrayBuffer())
        filePayloads.push({ base64, name: file.name })
      }

      setStatus('analyzing')
      await onAnalyzeDocuments(filePayloads)
      // Hand-off to parent's pre-flight modal succeeded — clear the spinner so
      // the file list is ready if the user cancels and comes back.
      setStatus('idle')
    } catch (err) {
      setParseError(err instanceof Error ? err.message : String(err))
      setStatus('idle')
    }
  }

  // Blocks an invalid roster before the backend; reports duplicates/similar names for acknowledgement.
  // JSON has no sheets or rows to check, so it goes straight through.
  const runPipelineGate = async (docFiles: File[], rosters: File[]) => {
    const [roster] = rosters
    if (!roster || !clientName.trim() || !onAnalyzeDocuments) {
      await runDocumentAnalysis(docFiles, rosters)
      return
    }

    setStatus('extracting')
    const base64 = arrayBufferToBase64(await roster.arrayBuffer())
    const { result, errors } = await validateRosterFile({ name: roster.name, base64 }, (b64) => api.parseExcel(b64))
    if (errors.length > 0) {
      setRosterErrors(errors)
      setStatus('idle')
      return
    }

    const duplicateRoles = result?.duplicateRoles ?? []
    const similarRoles = result?.similarRoles ?? []
    if (duplicateRoles.length > 0 || similarRoles.length > 0) {
      setRosterReport({ fileName: roster.name, duplicateRoles, similarRoles })
      pendingAfterRosterReportRef.current = () => { void runDocumentAnalysis(docFiles, rosters) }
      setStatus('idle')
      return
    }

    await runDocumentAnalysis(docFiles, rosters)
  }

  const processFiles = async (rosters: File[] = rosterFiles, docs: File[] = contextFiles) => {
    if (docs.length === 0 && rosters.length === 0) return
    setParseError(null)
    setRosterErrors([])

    // Files are never merged: the user picks one roster and confirms the rest are discarded.
    if (rosters.length > 1) {
      setRosterSelection({ rosters, docs })
      return
    }

    await runPipelineGate(docs, rosters)
  }

  function handleRosterReportContinue() {
    setRosterReport(null)
    const next = pendingAfterRosterReportRef.current
    pendingAfterRosterReportRef.current = null
    next?.()
  }

  function handleRosterReportCancel() {
    setRosterReport(null)
    pendingAfterRosterReportRef.current = null
    setStatus('idle')
  }

  function handleRosterSelectionConfirm(selectedName: string) {
    if (!rosterSelection) return
    const selected = rosterSelection.rosters.find((f) => f.name === selectedName)
    const docs = rosterSelection.docs
    setRosterSelection(null)
    if (!selected) return
    setRosterFiles([selected])
    void processFiles([selected], docs)
  }

  function handleRosterSelectionCancel() {
    setRosterSelection(null)
  }

  const onDrop = useCallback((e: React.DragEvent, zone: UploadZone) => {
    e.preventDefault()
    setDragging(null)
    addFiles(e.dataTransfer.files, zone)
  }, [])

  // No hidden <input> here: api.openFileDialog covers both platforms — Electron
  // opens the native dialog, the web client injects its own file input.
  const openDialog = async (zone: UploadZone) => {
    // Offer exactly what the zone accepts — ZONE_EXTS is the single source of
    // truth, so the dialog cannot surface a type addFiles would then reject.
    const filters = [{
      name: zone === 'roster' ? 'Employee Roster' : 'Supporting Documents',
      extensions: ZONE_EXTS[zone].map((e) => e.replace(/^\./, '')),
    }]
    const filePaths = await api.openFileDialog({
      properties: ['openFile', 'multiSelections'],
      filters,
    })
    if (filePaths) {
      const paths = Array.isArray(filePaths) ? filePaths : [filePaths]
      const errors: string[] = []
      for (const filePath of paths) {
        try {
          const { base64, name } = await api.readFile(filePath)
          const bytes = Uint8Array.from(atob(base64), (c) => c.charCodeAt(0))
          addFiles([new File([bytes], name)], zone)
        } catch (err) {
          errors.push(err instanceof Error ? err.message : String(err))
        }
      }
      if (errors.length) setParseError(`Could not read: ${errors.join(', ')}`)
    }
  }

  const isProcessing = status === 'extracting' || status === 'analyzing'
  // A gate modal is open over the zones; its continuation holds the file list it was opened with.
  const gateOpen = rosterSelection !== null || rosterReport !== null
  // Every roster file (XLSX, CSV, or JSON) and every document always goes
  // through the AI pipeline (WI 2211, PBI 2180) — there is no direct-import
  // path left, so this is both the submit count and the pipeline file count.
  const queuedCount = rosterFiles.length + contextFiles.length
  const blockedByMissingApiKey = queuedCount > 0 && !hasApiKey

  /**
   * One upload zone. The div carries button semantics and its own key handler so
   * the zone is reachable without a mouse — the previous single zone was a bare
   * div with onClick and could not be focused at all.
   */
  function renderZone(
    zone: UploadZone,
    title: string,
    accepts: string,
    hint: string,
    files: File[]
  ) {
    const isDragTarget = dragging === zone
    const isFocused = focusedZone === zone
    const error = zoneError?.zone === zone ? zoneError.message : null
    const locked = isProcessing || gateOpen
    const open = () => { if (!locked) void openDialog(zone) }

    return (
      <div>
        <div
          role="button"
          tabIndex={locked ? -1 : 0}
          aria-label={`${title}. ${accepts}. Press Enter to browse for files.`}
          aria-disabled={locked}
          onDrop={(e) => onDrop(e, zone)}
          onDragOver={(e) => { e.preventDefault(); setDragging(zone) }}
          onDragLeave={() => setDragging(null)}
          onClick={open}
          onFocus={() => setFocusedZone(zone)}
          onBlur={() => setFocusedZone((z) => (z === zone ? null : z))}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); open() }
          }}
          style={isFocused ? { outline: '2px solid #A100FF', outlineOffset: '2px' } : undefined}
          className={`border-2 border-dashed rounded-xl p-4 text-center transition-all h-full ${
            isProcessing ? 'border-purple-300 bg-purple-50 cursor-wait' :
            isDragTarget ? 'border-purple-400 bg-purple-50 cursor-copy' :
            error ? 'border-red-300 bg-red-50 cursor-pointer' :
            'border-gray-200 hover:border-gray-300 bg-gray-50 cursor-pointer'
          }`}
        >
          <div className="text-xl mb-1">{zone === 'roster' ? '📊' : '📄'}</div>
          {/* Label text is verbatim from the WI 2042 acceptance criteria. */}
          <div className="text-sm font-semibold text-gray-700 mb-0.5">{title}</div>
          <div className="text-[10px] text-gray-400 mt-1 leading-relaxed">{hint}</div>
        </div>

        {error && (
          <p className="mt-1.5 text-xs text-red-600 flex items-start gap-1">
            <span className="flex-shrink-0">✕</span>
            <span>{error}</span>
          </p>
        )}

        {files.length > 0 && (
          <div className="mt-2 space-y-1">
            <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
              {zone === 'roster' ? 'Roster file' : 'Context documents'} ({files.length})
            </p>
            {files.map((f) => (
              <div key={f.name} className="flex items-center justify-between px-2.5 py-1.5 bg-white border border-gray-200 rounded-lg text-xs">
                <span className="text-gray-700 truncate flex-1">{f.name}</span>
                <button
                  type="button"
                  aria-label={`Remove ${f.name}`}
                  disabled={gateOpen}
                  onClick={() => removeFile(f.name, zone)}
                  className="text-gray-400 hover:text-red-500 ml-2 text-base leading-none"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-2">Upload Client Documents or Data Files</label>

      {/* ── Two upload zones — roster and context documents ─────────────────── */}
      <div className="grid grid-cols-2 gap-3 items-start">
        {renderZone(
          'roster',
          'Employee Roster (XLSX/CSV/JSON)',
          'XLSX, CSV or a Talent Navigator JSON export',
          'One roster file only. XLSX/CSV: one row per role with Role (or Job Title) and Subfunction (or Function) columns; FTE / Headcount optional. For scoped rosters (HR, Finance, Tech), combine them into one workbook with one sheet per scope. JSON: a Talent Navigator export.',
          rosterFiles
        )}
        {renderZone(
          'context',
          'Supporting Documents (PDF, DOCX, PPTX)',
          'PDF, DOCX or PPTX',
          'Org charts, strategy docs, task inventories, slide decks. All are analyzed together for context.',
          contextFiles
        )}
      </div>

      {/* Submit — one action for both zones */}
      {queuedCount > 0 && !isProcessing && !gateOpen && (
        <>
          <button
            type="button"
            onClick={() => { void processFiles() }}
            disabled={blockedByMissingApiKey}
            title={blockedByMissingApiKey ? 'Add an API key in Settings before analyzing files.' : undefined}
            className="w-full mt-3 py-2 px-4 rounded-lg text-white text-sm font-semibold transition-all disabled:opacity-40 disabled:cursor-not-allowed"
            style={{ background: 'linear-gradient(135deg, #A100FF, #7900B8)' }}
          >
            {`Analyze ${queuedCount} file${queuedCount > 1 ? 's' : ''} →`}
          </button>
          {blockedByMissingApiKey && (
            <p className="mt-1.5 text-xs text-amber-700">
              ⚠️ Add an API key in <strong>Settings</strong> to analyze these files.
            </p>
          )}
        </>
      )}

      {/* Progress during AI analysis */}
      {status === 'analyzing' && (
        <div className="mt-3 text-xs text-purple-600 font-medium animate-pulse">
          ✨ Submitting documents for AI analysis...
        </div>
      )}

      {status === 'extracting' && (
        <div className="mt-3 text-xs text-purple-600 animate-pulse">📄 Extracting text from documents…</div>
      )}

      <RosterErrorList errors={rosterErrors} />

      {parseError && (
        <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <span className="text-red-500 mt-0.5 flex-shrink-0">✕</span>
          <p className="text-xs text-red-700 whitespace-pre-wrap flex-1">{parseError}</p>
          <button
            type="button"
            onClick={() => setParseError(null)}
            className="text-red-400 hover:text-red-600 flex-shrink-0 text-base leading-none ml-1"
            aria-label="Dismiss"
          >
            ×
          </button>
        </div>
      )}

      {rosterSelection && (
        <RosterSelectionModal
          rosterNames={rosterSelection.rosters.map((f) => f.name)}
          onConfirm={handleRosterSelectionConfirm}
          onCancel={handleRosterSelectionCancel}
        />
      )}

      {rosterReport && (
        <RosterQualityModal
          fileName={rosterReport.fileName}
          duplicateRoles={rosterReport.duplicateRoles}
          similarRoles={rosterReport.similarRoles}
          onContinue={handleRosterReportContinue}
          onCancel={handleRosterReportCancel}
        />
      )}
    </div>
  )
}
